import React, { useState, useRef, useCallback } from 'react';
import { ProgressBar } from 'primereact/progressbar';
import { useNavigate } from 'react-router-dom';
import {
  Upload, ArrowLeft, ArrowRight, ArrowDown, Info, Check, X, FileSpreadsheet,
  TriangleAlert, RefreshCw, CircleAlert, CircleX, Download, SkipForward, Rows3, Copy,
} from 'lucide-react';
import apiClient from '../../stateManagers/apiSetup/axiosInterceptor';
import { channelLabel } from '../Leads/parts/leadOrigin';

// ── Design tokens (matches Leads.tsx) ────────────────────────────────────────
const C = {
  teal:       '#115550',
  tealMid:    '#16706a',
  tealLight:  '#c6e3e0',
  lime:       '#c8e94b',
  limeDim:    'rgba(200,233,75,0.18)',
  limeBorder: 'rgba(200,233,75,0.4)',
  white:      '#ffffff',
  border:     '#E5E7EB',
  bg:         '#f4faf9',
  muted:      '#9ca3af',
  sub:        '#6b7280',
};

// ── Grey border system (outer shell ring + inner card) ────────────────────────
const G = {
  shellBg:     '#f8f9f9',
  shellBorder: '#e8ebeb',
  line:        '#e5e7eb',
  soft:        '#eef0f0',
  softer:      '#f2f4f4',
};
const SHELL: React.CSSProperties = {
  background: G.shellBg,
  border: `1px solid ${G.shellBorder}`,
  borderRadius: '13px',
  padding: '4.6px',
};
const INNER: React.CSSProperties = {
  background: C.white,
  border: `1px solid ${G.line}`,
  borderRadius: '9.2px',
};
const LABEL: React.CSSProperties = {
  fontSize: '0.6rem', fontWeight: 600, letterSpacing: '0.5px', textTransform: 'uppercase', color: C.sub,
};
const iconBox = (bg = 'white', border: string = G.line): React.CSSProperties => ({
  width: '31px', height: '31px', borderRadius: '8.3px', background: bg, border: `1px solid ${border}`,
  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
});

// ── Types ─────────────────────────────────────────────────────────────────────
interface ImportResult {
  success: boolean;
  message: string;
  inserted_count: number;
  skipped_count: number;
  /** Rows whose lead id (e.g. Meta's `id`) is already a lead — skipped. */
  duplicate_count?: number;
  /** meta | google_form | csv — detected from the headers */
  channel?: string;
  issues: string[];
}

type UploadState = 'idle' | 'dragging' | 'uploading' | 'done' | 'error';

// ── Supported column mappings (mirrors backend csvHeaderMap) ──────────────────
const COLUMN_GUIDE = [
  { canonical: 'name',          aliases: ['name', 'full_name'],                                                   required: true  },
  { canonical: 'phone',         aliases: ['phone', 'phone_no', 'phone_number', 'mobile', 'mobile_no', 'contact'], required: true  },
  { canonical: 'email',         aliases: ['email', 'email_id', 'email_address'],                                  required: false },
  { canonical: 'source',        aliases: ['source', 'lead_source', 'referred_by', 'reference'],                   required: false },
  { canonical: 'status',        aliases: ['status', 'lead_status'],                                               required: false },
  { canonical: 'notes',         aliases: ['notes', 'remarks', 'comment', 'comments'],                             required: false },
  { canonical: 'city',          aliases: ['city', 'location', 'district'],                                        required: false },
  { canonical: 'interested_in', aliases: ['interested_in', 'product', 'product_name', 'course', 'service', 'interest'], required: false },
];

// ── Where the lead came from (mirrors models/origin.go aliases) ──────────────
// All optional. Saved on the lead and used by the Campaign / Form filters.
const ORIGIN_GUIDE = [
  { field: 'campaign',     note: 'Campaign id + name',        aliases: ['campaign_id', 'campaign_name', 'campaign'] },
  { field: 'form',         note: 'Form id + name',            aliases: ['form_id', 'form_name', 'form'] },
  { field: 'lead id',      note: 'Stops duplicates',          aliases: ['id', 'lead_id', 'leadgen_id', 'external_id'] },
  { field: 'submitted at', note: 'When the form was filled',  aliases: ['created_time', 'timestamp', 'submitted_at'] },
  { field: 'extras',       note: 'Shown on the lead',         aliases: ['ad_name', 'adset_name', 'platform', 'is_organic', 'utm_source', 'utm_campaign', 'utm_*'] },
];

// ─────────────────────────────────────────────────────────────────────────────

const ImportLeads: React.FC = () => {
  const navigate     = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [uploadState, setUploadState]   = useState<UploadState>('idle');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [progress, setProgress]         = useState(0);
  const [result, setResult]             = useState<ImportResult | null>(null);
  const [errorMsg, setErrorMsg]         = useState<string | null>(null);
  const [showGuide, setShowGuide]       = useState(false);

  // ── File validation (client-side) ─────────────────────────────────────────
  const validateFile = (file: File): string | null => {
    const ext = file.name.split('.').pop()?.toLowerCase();
    if (ext !== 'csv') return 'Only .csv files are supported.';
    if (file.size > 5 * 1024 * 1024) return 'File must be under 5 MB.';
    return null;
  };

  const handleFileSelect = (file: File) => {
    const err = validateFile(file);
    if (err) { setErrorMsg(err); return; }
    setErrorMsg(null);
    setResult(null);
    setSelectedFile(file);
    setUploadState('idle');
  };

  // ── Drag & drop ───────────────────────────────────────────────────────────
  const onDragOver  = (e: React.DragEvent) => { e.preventDefault(); setUploadState('dragging'); };
  const onDragLeave = ()                   => { if (uploadState === 'dragging') setUploadState('idle'); };
  const onDrop      = (e: React.DragEvent) => {
    e.preventDefault();
    setUploadState('idle');
    const file = e.dataTransfer.files[0];
    if (file) handleFileSelect(file);
  };

  // ── Upload ────────────────────────────────────────────────────────────────
  const handleUpload = useCallback(async () => {
    if (!selectedFile) return;
    setUploadState('uploading');
    setProgress(0);
    setResult(null);
    setErrorMsg(null);

    const ticker = setInterval(() => {
      setProgress(p => (p < 85 ? p + Math.random() * 12 : p));
    }, 280);

    const form = new FormData();
    form.append('csv_file', selectedFile);

    try {
      const res = await apiClient.post<ImportResult>('/v1/import', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      clearInterval(ticker);
      setProgress(100);
      setResult(res.data);
      setUploadState('done');
    } catch (e: any) {
      clearInterval(ticker);
      setProgress(0);
      const msg = e.response?.data?.error || 'Upload failed. Please try again.';
      setErrorMsg(msg);
      setResult(e.response?.data ?? null);
      setUploadState('error');
    }
  }, [selectedFile]);

  const handleReset = () => {
    setSelectedFile(null);
    setUploadState('idle');
    setResult(null);
    setErrorMsg(null);
    setProgress(0);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // ── Derived ───────────────────────────────────────────────────────────────
  const isDragging  = uploadState === 'dragging';
  const isUploading = uploadState === 'uploading';
  const isDone      = uploadState === 'done';
  const isError     = uploadState === 'error';
  const hasFile     = !!selectedFile;

  const fmtSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  const sectionTitle = (text: string, extra?: React.ReactNode) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '7.4px' }}>
      <div style={{ width: '3px', height: '14.8px', background: `linear-gradient(180deg,${C.lime},#8aaa00)`, borderRadius: '3px' }} />
      <span style={{ fontSize: '0.78rem', fontWeight: 700, color: C.teal }}>{text}</span>
      {extra}
    </div>
  );

  const stat = (label: string, value: number, color: string, bg: string, border: string, icon: React.ReactNode) => (
    <div style={{ ...SHELL, flex: '1 1 129px', borderRadius: '11px', padding: '3.7px' }}>
      <div style={{ ...INNER, borderRadius: '8.3px', padding: '11px 12.9px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '9.2px' }}>
        <div>
          <div style={LABEL}>{label}</div>
          <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#111827', lineHeight: 1.1, marginTop: '4.6px' }}>{value}</div>
        </div>
        <div style={iconBox(bg, border)}>{icon}</div>
      </div>
    </div>
  );

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <>
      <style>{`
        .imp-zone-base { transition: all 0.22s ease; }
        .imp-zone-base:hover { border-color: rgba(17,85,80,0.35) !important; background: rgba(17,85,80,0.025) !important; }
        .imp-btn-lime:hover  { transform: translateY(-1px); box-shadow: 0 4px 14px rgba(200,233,75,0.5) !important; }
        .imp-btn-ghost:hover { background: rgba(17,85,80,0.08) !important; color: #115550 !important; }
        .imp-issue-row { animation: fadeSlideIn 0.18s ease both; }
        @keyframes fadeSlideIn { from { opacity:0; transform:translateY(4px); } to { opacity:1; transform:translateY(0); } }
        .imp-guide-row:nth-child(even) { background: rgba(17,85,80,0.02); }
        .imp-guide-row:hover { background: rgba(200,233,75,0.1); }
        .imp-spinner { animation: spin 0.9s linear infinite; }
        @keyframes spin { to { transform: rotate(360deg); } }
        .imp-progress.p-progressbar { background: ${G.soft}; border-radius: 20px; overflow: hidden; }
        .imp-progress .p-progressbar-value { background: ${C.teal}; }
      `}</style>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '11px' }}>

        {/* ── Page header ─────────────────────────────────────────────── */}
        <div style={SHELL}>
          <div style={{ ...INNER, padding: '12px 16.6px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '9.2px' }}>
            <div>
              <div style={LABEL}>Import</div>
              <h1 style={{ margin: '3.7px 0 0', fontSize: '1.01rem', fontWeight: 700, color: '#111827', letterSpacing: '-0.2px' }}>Import Leads</h1>
            </div>
            <button onClick={() => navigate('/leads')} className="imp-btn-ghost"
              style={{ display: 'inline-flex', alignItems: 'center', gap: '4.6px', padding: '5.5px 12px', background: G.shellBg, border: `1px solid ${G.line}`, borderRadius: '7.4px', color: C.sub, fontSize: '0.7rem', fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s' }}>
              <ArrowLeft size={12} /> Back to Leads
            </button>
          </div>
        </div>

        {/* ── Required columns notice ──────────────────────────────────── */}
        <div style={SHELL}>
          <div style={{ ...INNER, padding: '10.1px 13px', display: 'flex', alignItems: 'flex-start', gap: '9.2px' }}>
            <div style={iconBox('rgba(17,85,80,0.06)', 'rgba(17,85,80,0.15)')}>
              <Info size={14} color={C.teal} />
            </div>
            <p style={{ margin: 0, fontSize: '0.71rem', color: '#374151', lineHeight: 1.6, alignSelf: 'center' }}>
              Your CSV <strong>must</strong> include a <code style={{ background: 'rgba(17,85,80,0.08)', color: C.teal, padding: '1px 4.6px', borderRadius: '4px' }}>name</code> column and a <code style={{ background: 'rgba(17,85,80,0.08)', color: C.teal, padding: '1px 4.6px', borderRadius: '4px' }}>phone</code> column — rows missing either will be skipped. All other columns are optional:
              campaign / form columns (like a Meta lead export) are saved as where the lead came from, and any other column is kept on the lead
              as a custom field.{' '}
              <button onClick={() => setShowGuide(g => !g)} style={{ background: 'none', border: 'none', color: C.tealMid, fontWeight: 700, cursor: 'pointer', fontSize: '0.71rem', padding: 0, textDecoration: 'underline', textDecorationColor: C.lime }}>
                {showGuide ? 'Hide' : 'View'} column guide ↓
              </button>
            </p>
          </div>
        </div>

        {/* ── Upload card ─────────────────────────────────────────────── */}
        <div style={SHELL}>
          <div style={{ ...INNER, overflow: 'hidden' }}>
            <div style={{ padding: '11px 16.6px', borderBottom: `1px solid ${G.soft}` }}>
              {sectionTitle('Upload CSV File')}
            </div>

            <div style={{ padding: '18.4px 22px' }}>

              {/* Drop area */}
              <div
                className="imp-zone-base"
                onDragOver={onDragOver}
                onDragLeave={onDragLeave}
                onDrop={onDrop}
                onClick={() => !isUploading && fileInputRef.current?.click()}
                style={{
                  border: `1.5px dashed ${isDragging ? C.teal : isDone ? '#16a34a' : isError ? '#dc2626' : 'rgba(17,85,80,0.25)'}`,
                  borderRadius: '10.1px',
                  background: isDragging ? 'rgba(17,85,80,0.04)' : isDone ? 'rgba(22,163,74,0.04)' : isError ? 'rgba(220,38,38,0.03)' : C.bg,
                  padding: '33px 22px',
                  textAlign: 'center',
                  cursor: isUploading ? 'not-allowed' : 'pointer',
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                {/* dot grid */}
                <div style={{ position: 'absolute', inset: 0, backgroundImage: 'radial-gradient(rgba(17,85,80,0.06) 1px,transparent 1px)', backgroundSize: '20px 20px', pointerEvents: 'none', opacity: isDragging ? 1 : 0.5, transition: 'opacity 0.2s' }} />

                <input ref={fileInputRef} type="file" accept=".csv" style={{ display: 'none' }}
                  onChange={e => { const f = e.target.files?.[0]; if (f) handleFileSelect(f); }} />

                {/* Icon */}
                <div style={{ marginBottom: '11px', position: 'relative', zIndex: 1 }}>
                  {isUploading ? (
                    <div style={{ width: '48px', height: '48px', border: `3px solid ${C.tealLight}`, borderTopColor: C.teal, borderRadius: '50%', margin: '0 auto' }} className="imp-spinner" />
                  ) : isDone ? (
                    <div style={{ width: '48px', height: '48px', background: 'rgba(22,163,74,0.1)', border: '1.5px solid rgba(22,163,74,0.3)', borderRadius: '50%', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Check size={22} color="#16a34a" />
                    </div>
                  ) : isError ? (
                    <div style={{ width: '48px', height: '48px', background: 'rgba(220,38,38,0.08)', border: '1.5px solid rgba(220,38,38,0.3)', borderRadius: '50%', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <X size={22} color="#dc2626" />
                    </div>
                  ) : (
                    <div style={{ width: '48px', height: '48px', background: 'rgba(17,85,80,0.08)', border: '1.5px solid rgba(17,85,80,0.15)', borderRadius: '50%', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s', transform: isDragging ? 'scale(1.08)' : 'scale(1)' }}>
                      {isDragging ? <ArrowDown size={22} color={C.teal} /> : <FileSpreadsheet size={22} color={C.teal} />}
                    </div>
                  )}
                </div>

                {/* Text */}
                <div style={{ position: 'relative', zIndex: 1 }}>
                  {isUploading ? (
                    <p style={{ margin: '0 0 3.7px', color: C.teal, fontWeight: 700, fontSize: '0.85rem' }}>Uploading…</p>
                  ) : isDone ? (
                    <p style={{ margin: '0 0 3.7px', color: '#16a34a', fontWeight: 700, fontSize: '0.85rem' }}>Upload complete!</p>
                  ) : isError && !hasFile ? (
                    <p style={{ margin: '0 0 3.7px', color: '#dc2626', fontWeight: 700, fontSize: '0.85rem' }}>Upload failed</p>
                  ) : hasFile ? (
                    <p style={{ margin: '0 0 3.7px', color: C.teal, fontWeight: 700, fontSize: '0.85rem' }}>{selectedFile!.name}</p>
                  ) : isDragging ? (
                    <p style={{ margin: '0 0 3.7px', color: C.teal, fontWeight: 700, fontSize: '0.85rem' }}>Drop it!</p>
                  ) : (
                    <p style={{ margin: '0 0 3.7px', color: C.teal, fontWeight: 700, fontSize: '0.85rem' }}>
                      Drag & drop your CSV here, or <span style={{ textDecoration: 'underline', textDecorationColor: C.lime }}>browse</span>
                    </p>
                  )}
                  {hasFile && !isDone && !isUploading && (
                    <p style={{ margin: '0 0 2px', color: C.sub, fontSize: '0.7rem' }}>{fmtSize(selectedFile!.size)} · CSV</p>
                  )}
                  {!hasFile && !isUploading && !isDone && (
                    <p style={{ margin: 0, color: C.muted, fontSize: '0.7rem' }}>Supported: .csv · Max 5 MB</p>
                  )}
                  {isDone && result && (
                    <p style={{ margin: 0, color: C.sub, fontSize: '0.7rem' }}>
                      {result.inserted_count} leads inserted · {result.skipped_count} skipped
                      {(result.duplicate_count ?? 0) > 0 && ` · ${result.duplicate_count} already in leads`}
                      {result.channel && result.channel !== 'csv' && ` · detected ${channelLabel(result.channel)}`}
                    </p>
                  )}
                </div>
              </div>

              {/* Progress bar */}
              {isUploading && (
                <div style={{ marginTop: '12.9px' }}>
                  <ProgressBar className="imp-progress" value={Math.round(progress)} style={{ height: '5.5px' }} showValue={false} />
                  <p style={{ margin: '4.6px 0 0', fontSize: '0.66rem', color: C.muted, textAlign: 'right' }}>{Math.round(progress)}%</p>
                </div>
              )}

              {/* Error banner */}
              {errorMsg && (
                <div style={{ marginTop: '11px', padding: '9.2px 12.9px', background: 'rgba(220,38,38,0.06)', border: '1px solid rgba(220,38,38,0.22)', borderRadius: '8.3px', display: 'flex', alignItems: 'flex-start', gap: '7.4px' }}>
                  <TriangleAlert size={14} color="#dc2626" style={{ marginTop: '1px', flexShrink: 0 }} />
                  <span style={{ fontSize: '0.74rem', color: '#c62828', fontWeight: 600 }}>{errorMsg}</span>
                </div>
              )}

              {/* Action buttons */}
              <div style={{ display: 'flex', gap: '7.4px', marginTop: '14.8px', flexWrap: 'wrap' }}>
                {hasFile && !isDone && !isUploading && (
                  <button onClick={handleUpload} className="imp-btn-lime"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '4.6px', padding: '7.4px 18.4px', background: C.lime, border: 'none', borderRadius: '7.4px', color: C.teal, fontSize: '0.74rem', fontWeight: 700, cursor: 'pointer', boxShadow: '0 2px 7px rgba(200,233,75,0.3)', transition: 'all 0.2s', letterSpacing: '0.2px' }}>
                    <Upload size={13} /> IMPORT NOW
                  </button>
                )}
                {(hasFile || isDone || isError) && (
                  <button onClick={handleReset} className="imp-btn-ghost"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '4.6px', padding: '7.4px 14.8px', background: G.shellBg, border: `1px solid ${G.line}`, borderRadius: '7.4px', color: C.sub, fontSize: '0.72rem', fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s' }}>
                    <RefreshCw size={12} /> {isDone ? 'Import Another' : 'Clear'}
                  </button>
                )}
                {isDone && (
                  <button onClick={() => navigate('/leads')}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '4.6px', padding: '7.4px 16.6px', background: 'rgb(17,85,80)', border: 'none', borderRadius: '7.4px', color: 'white', fontSize: '0.72rem', fontWeight: 700, cursor: 'pointer', boxShadow: '0 2px 8px rgba(17,85,80,0.25)', transition: 'all 0.2s' }}
                    onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.background = 'rgb(22,112,106)'; }}
                    onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.background = 'rgb(17,85,80)'; }}>
                    <ArrowRight size={12} /> VIEW LEADS
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ── Result summary ───────────────────────────────────────────── */}
        {result && (
          <div style={SHELL}>
            <div style={{ ...INNER, overflow: 'hidden' }}>
              <div style={{ padding: '11px 16.6px', borderBottom: `1px solid ${G.soft}` }}>
                {sectionTitle('Import Summary')}
              </div>
              <div style={{ padding: '14.8px 16.6px' }}>
                <div style={{ display: 'flex', gap: '9.2px', flexWrap: 'wrap', marginBottom: result.issues?.length ? '14.8px' : 0 }}>
                  {stat('Leads Imported', result.inserted_count, '#16a34a', 'rgba(22,163,74,0.08)', 'rgba(22,163,74,0.25)', <Download size={14} color="#16a34a" />)}
                  {stat('Rows Skipped', result.skipped_count, '#dc2626', 'rgba(239,68,68,0.07)', 'rgba(239,68,68,0.22)', <SkipForward size={14} color="#dc2626" />)}
                  {(result.duplicate_count ?? 0) > 0 &&
                    stat('Already in Leads', result.duplicate_count ?? 0, '#b45309', 'rgba(245,158,11,0.1)', 'rgba(245,158,11,0.3)', <Copy size={14} color="#b45309" />)}
                  {stat('Total Rows Processed', result.inserted_count + result.skipped_count + (result.duplicate_count ?? 0), C.teal, 'rgba(17,85,80,0.06)', 'rgba(17,85,80,0.18)', <Rows3 size={14} color={C.teal} />)}
                </div>

                {result.issues && result.issues.length > 0 && (
                  <div>
                    <p style={{ margin: '0 0 7.4px', fontSize: '0.72rem', fontWeight: 700, color: '#c62828', display: 'flex', alignItems: 'center', gap: '4.6px' }}>
                      <CircleAlert size={13} />
                      {result.issues.length} row{result.issues.length > 1 ? 's' : ''} had issues:
                    </p>
                    <div style={{ maxHeight: '203px', overflowY: 'auto', borderRadius: '7.4px', border: '1px solid rgba(220,38,38,0.2)', background: 'rgba(220,38,38,0.03)' }}>
                      {result.issues.map((issue, i) => (
                        <div key={i} className="imp-issue-row"
                          style={{ padding: '7.4px 11px', borderBottom: i < result.issues.length - 1 ? '1px solid rgba(220,38,38,0.1)' : 'none', fontSize: '0.7rem', color: '#b91c1c', display: 'flex', gap: '6.5px', alignItems: 'flex-start', animationDelay: `${i * 0.03}s` }}>
                          <CircleX size={12} color="#ef4444" style={{ marginTop: '1px', flexShrink: 0 }} />
                          {issue}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ── Column guide (collapsible) ───────────────────────────────── */}
        {showGuide && (
          <div style={SHELL}>
            <div style={{ ...INNER, overflow: 'hidden' }}>
              <div style={{ padding: '11px 16.6px', borderBottom: `1px solid ${G.soft}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                {sectionTitle('Supported Column Names',
                  <span style={{ padding: '1px 6.5px', borderRadius: '20px', background: C.limeDim, color: '#4a5e00', fontSize: '0.63rem', fontWeight: 700, border: `1px solid ${C.limeBorder}` }}>
                    {COLUMN_GUIDE.length} fields
                  </span>
                )}
                <button onClick={() => setShowGuide(false)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.muted, display: 'flex', alignItems: 'center', gap: '2.8px', fontSize: '0.67rem', fontWeight: 600 }}>
                  <X size={12} /> Close
                </button>
              </div>

              <div>
                <p style={{ padding: '9.2px 16.6px 6px', margin: 0, fontSize: '0.7rem', color: C.sub }}>
                  Headers are normalised (lowercased, spaces → underscores). Any alias below maps to the canonical field.
                </p>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.71rem' }}>
                    <thead>
                      <tr style={{ background: '#fafbfb' }}>
                        {['Field', 'Required', 'Accepted Header Names'].map(h => (
                          <th key={h} style={{ padding: '7.4px 13px', textAlign: 'left', color: C.muted, fontWeight: 700, fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.5px', borderTop: `1px solid ${G.soft}`, borderBottom: `1px solid ${G.soft}` }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {COLUMN_GUIDE.map(col => (
                        <tr key={col.canonical} className="imp-guide-row" style={{ transition: 'background 0.15s' }}>
                          <td style={{ padding: '8.3px 13px', fontWeight: 700, color: C.teal, whiteSpace: 'nowrap', borderBottom: `1px solid ${G.softer}` }}>
                            <code style={{ background: 'rgba(17,85,80,0.07)', padding: '1.8px 5.5px', borderRadius: '4px', fontSize: '0.7rem' }}>{col.canonical}</code>
                          </td>
                          <td style={{ padding: '8.3px 13px', borderBottom: `1px solid ${G.softer}` }}>
                            {col.required
                              ? <span style={{ padding: '1.8px 6.5px', borderRadius: '20px', background: 'rgba(220,38,38,0.08)', color: '#c62828', border: '1px solid rgba(220,38,38,0.25)', fontSize: '0.62rem', fontWeight: 700 }}>Required</span>
                              : <span style={{ padding: '1.8px 6.5px', borderRadius: '20px', background: 'rgba(156,163,175,0.12)', color: C.sub, border: '1px solid rgba(156,163,175,0.3)', fontSize: '0.62rem', fontWeight: 600 }}>Optional</span>
                            }
                          </td>
                          <td style={{ padding: '8.3px 13px', color: C.sub, borderBottom: `1px solid ${G.softer}` }}>
                            <div style={{ display: 'flex', gap: '4.6px', flexWrap: 'wrap' }}>
                              {col.aliases.map(a => (
                                <code key={a} style={{ background: 'rgba(200,233,75,0.15)', border: '1px solid rgba(200,233,75,0.4)', borderRadius: '4px', padding: '1px 5.5px', fontSize: '0.66rem', color: '#4a5e00' }}>{a}</code>
                              ))}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Where the lead came from */}
                <p style={{ padding: '14px 16.6px 6px', margin: 0, fontSize: '0.7rem', color: C.sub }}>
                  <strong style={{ color: C.teal }}>Where the lead came from</strong> — optional. Saved on the lead and used by the Campaign and Form
                  filters on the Leads page.
                </p>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.71rem' }}>
                    <thead>
                      <tr style={{ background: '#fafbfb' }}>
                        {['Saved as', 'What it does', 'Accepted Header Names'].map(h => (
                          <th key={h} style={{ padding: '7.4px 13px', textAlign: 'left', color: C.muted, fontWeight: 700, fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.5px', borderTop: `1px solid ${G.soft}`, borderBottom: `1px solid ${G.soft}` }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {ORIGIN_GUIDE.map(o => (
                        <tr key={o.field} className="imp-guide-row" style={{ transition: 'background 0.15s' }}>
                          <td style={{ padding: '8.3px 13px', fontWeight: 700, color: '#1d4ed8', whiteSpace: 'nowrap', borderBottom: `1px solid ${G.softer}` }}>
                            <code style={{ background: 'rgba(59,130,246,0.1)', padding: '1.8px 5.5px', borderRadius: '4px', fontSize: '0.7rem' }}>{o.field}</code>
                          </td>
                          <td style={{ padding: '8.3px 13px', color: C.sub, whiteSpace: 'nowrap', borderBottom: `1px solid ${G.softer}` }}>{o.note}</td>
                          <td style={{ padding: '8.3px 13px', color: C.sub, borderBottom: `1px solid ${G.softer}` }}>
                            <div style={{ display: 'flex', gap: '4.6px', flexWrap: 'wrap' }}>
                              {o.aliases.map(a => (
                                <code key={a} style={{ background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.3)', borderRadius: '4px', padding: '1px 5.5px', fontSize: '0.66rem', color: '#1d4ed8' }}>{a}</code>
                              ))}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div style={{ padding: '11px 16.6px', display: 'flex', alignItems: 'flex-start', gap: '6.5px' }}>
                  <Info size={13} color={C.teal} style={{ flexShrink: 0, marginTop: '1px' }} />
                  <p style={{ margin: 0, fontSize: '0.69rem', color: C.sub, lineHeight: 1.6 }}>
                    Rows missing <strong>name</strong> or <strong>phone</strong>, or with an invalid email/phone format, will be skipped and listed in the summary.
                    Empty optional cells are stored as <code style={{ background: 'rgba(17,85,80,0.06)', padding: '1px 4.6px', borderRadius: '3px' }}>null</code>.
                    Any other column (e.g. <code style={{ background: 'rgba(17,85,80,0.06)', padding: '1px 4.6px', borderRadius: '3px' }}>Preferred Batch</code>) is kept
                    on the lead as a custom field. Rows whose lead id is already in your leads are skipped as duplicates. A Meta <code style={{ background: 'rgba(17,85,80,0.06)', padding: '1px 4.6px', borderRadius: '3px' }}>platform</code> value
                    (fb / ig) fills an empty source.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

      </div>
    </>
  );
};

export default ImportLeads;